import type { ReactNode } from "react";

export type AppRole = "super" | "admin" | "manager";

export type RoleOption = {
	label: string;
	value: AppRole;
};

export type NavItem = {
	path: string;
	label: string;
	icon: ReactNode;
	roles?: AppRole[];
	badge?: string;
	subtitle?: string;
};

export type NavGroup = {
	title: string;
	items: NavItem[];
};

export type RouteAccessRule = {
	label: string;
	roles?: AppRole[];
	subtitle?: string;
};

type NavIconName =
	| "alert"
	| "audit"
	| "bank"
	| "building"
	| "calendar"
	| "cart"
	| "chart"
	| "dashboard"
	| "file"
	| "grid"
	| "message"
	| "org"
	| "percent"
	| "plus"
	| "rooms"
	| "send"
	| "settings"
	| "shield"
	| "team"
	| "trophy"
	| "user"
	| "wallet";

const navIconPaths: Record<NavIconName, ReactNode> = {
	alert: (
		<>
			<path d="M12 4 21 20H3L12 4Z" />
			<path d="M12 9v5M12 17h.01" />
		</>
	),
	audit: (
		<>
			<path d="M12 3 19 6v5c0 4.6-3 7.4-7 9-4-1.6-7-4.4-7-9V6l7-3Z" />
			<path d="M9 12l2 2 4-5" />
		</>
	),
	bank: (
		<>
			<path d="M4 10h16M6 10v9M10 10v9M14 10v9M18 10v9M3 19h18" />
			<path d="M12 4 21 10H3l9-6Z" />
		</>
	),
	building: (
		<>
			<rect height="17" rx="2" width="14" x="5" y="4" />
			<path d="M9 8h.01M12 8h.01M15 8h.01M9 12h.01M12 12h.01M15 12h.01" />
			<path d="M10 21v-4h4v4" />
		</>
	),
	calendar: (
		<>
			<rect height="17" rx="2" width="18" x="3" y="5" />
			<path d="M8 3v4M16 3v4M3 10h18" />
		</>
	),
	cart: (
		<>
			<path d="M4 5h2l2 10h10l2-7H7" />
			<circle cx="10" cy="19" r="1.5" />
			<circle cx="17" cy="19" r="1.5" />
		</>
	),
	chart: <path d="M4 20V6M9 20V10M14 20V4M19 20v-8M3 20h18" />,
	dashboard: (
		<>
			<rect height="8" rx="1.7" width="8" x="4" y="4" />
			<rect height="5" rx="1.7" width="8" x="14" y="4" />
			<rect height="8" rx="1.7" width="8" x="14" y="12" />
			<rect height="5" rx="1.7" width="8" x="4" y="15" />
		</>
	),
	file: (
		<>
			<path d="M7 3h8l4 4v14H7V3Z" />
			<path d="M15 3v5h5M10 13h6M10 17h5" />
		</>
	),
	grid: (
		<>
			<rect height="6" rx="1.4" width="6" x="4" y="4" />
			<rect height="6" rx="1.4" width="6" x="14" y="4" />
			<rect height="6" rx="1.4" width="6" x="4" y="14" />
			<rect height="6" rx="1.4" width="6" x="14" y="14" />
		</>
	),
	message: (
		<>
			<path d="M5 6h14v10H9l-4 4V6Z" />
			<path d="M9 10h6M9 13h4" />
		</>
	),
	org: (
		<>
			<rect height="13" rx="2" width="16" x="4" y="8" />
			<path d="M9 8V5h6v3M8 13h8M8 17h5" />
		</>
	),
	percent: (
		<>
			<path d="M19 5 5 19" />
			<circle cx="7.5" cy="7.5" r="2.2" />
			<circle cx="16.5" cy="16.5" r="2.2" />
		</>
	),
	plus: <path d="M12 5v14M5 12h14" />,
	rooms: (
		<>
			<path d="M4 4h16v16H4V4Z" />
			<path d="M4 11h16M11 4v16M16 11v9" />
		</>
	),
	send: (
		<>
			<path d="M21 4 10 15" />
			<path d="m21 4-6 18-5-7-8-4 19-7Z" />
		</>
	),
	settings: (
		<>
			<path d="M4 7h9M17 7h3M4 17h3M11 17h9" />
			<circle cx="15" cy="7" r="2" />
			<circle cx="9" cy="17" r="2" />
		</>
	),
	shield: (
		<>
			<path d="M12 3 19 6v5c0 4.6-3 7.4-7 9-4-1.6-7-4.4-7-9V6l7-3Z" />
			<path d="M12 9v4" />
		</>
	),
	team: (
		<>
			<circle cx="9" cy="8" r="3" />
			<path d="M4 20v-1a5 5 0 0 1 10 0v1" />
			<path d="M15 11a3 3 0 1 0 0-6M17 20v-1a5 5 0 0 0-3-4.6" />
		</>
	),
	trophy: (
		<>
			<path d="M8 4h8v4a4 4 0 0 1-8 0V4ZM12 12v5M9 21h6" />
			<path d="M8 6H4v2a3 3 0 0 0 3 3M16 6h4v2a3 3 0 0 1-3 3" />
		</>
	),
	user: (
		<>
			<circle cx="12" cy="8" r="3.5" />
			<path d="M5 20v-1a7 7 0 0 1 14 0v1" />
		</>
	),
	wallet: (
		<>
			<rect height="13" rx="2.5" width="18" x="3" y="7" />
			<path d="M3 10h18M16 15h3" />
		</>
	),
};

function NavIcon({ name }: { name: NavIconName }) {
	return (
		<svg
			aria-hidden="true"
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth="1.8"
			viewBox="0 0 24 24"
		>
			{navIconPaths[name]}
		</svg>
	);
}

export const roleOptions: RoleOption[] = [
	{ label: "Super Admin", value: "super" },
	{ label: "Org Admin", value: "admin" },
	{ label: "Menejer", value: "manager" },
];

export const navGroups: NavGroup[] = [
	{
		title: "BOSH",
		items: [
			{
				path: "/dashboard",
				label: "Bosh sahifa",
				icon: <NavIcon name="dashboard" />,
				subtitle: "Umumiy ko'rsatkichlar",
			},
		],
	},
	{
		title: "SUPER ADMIN",
		items: [
			{
				path: "/organizations",
				label: "Tashkilotlar",
				icon: <NavIcon name="org" />,
				roles: ["super"],
				subtitle: "Barcha kompaniyalar",
			},
		],
	},
	{
		title: "OBYEKTLAR",
		items: [
			{ path: "/objects", label: "Binolar", icon: <NavIcon name="building" /> },
			{ path: "/rooms", label: "Xonalar xaritasi", icon: <NavIcon name="grid" /> },
		],
	},
	{
		title: "MIJOZLAR",
		items: [{ path: "/clients", label: "Mijozlar", icon: <NavIcon name="team" /> }],
	},
	{
		title: "LEADLAR",
		items: [{ path: "/leads", label: "Leadlar (kanban)", icon: <NavIcon name="team" /> }],
	},
	{
		title: "SOTUV",
		items: [{ path: "/sales", label: "Sotuvlar", icon: <NavIcon name="cart" /> }],
	},
	{
		title: "TO'LOVLAR",
		items: [{ path: "/payments", label: "To'lovlar", icon: <NavIcon name="wallet" /> }],
	},
	{
		title: "SMS",
		items: [
			{
				path: "/sms/balance",
				label: "Balans",
				icon: <NavIcon name="message" />,
				roles: ["admin"],
			},
		],
	},
	{
		title: "HISOBOTLAR",
		items: [
			{
				path: "/reports/sales",
				label: "Sotuv hisoboti",
				icon: <NavIcon name="chart" />,
				roles: ["admin", "super"],
			},
			{
				path: "/reports/debts",
				label: "Qarzdorlik",
				icon: <NavIcon name="alert" />,
				roles: ["admin", "super", "manager"],
			},
			{
				path: "/reports/payments",
				label: "Kassa hisoboti",
				icon: <NavIcon name="wallet" />,
				roles: ["admin", "super"],
			},
			{
				path: "/reports/managers",
				label: "Menejerlar reytingi",
				icon: <NavIcon name="trophy" />,
				roles: ["admin", "super"],
			},
			{
				path: "/reports/rooms",
				label: "Xonalar holati",
				icon: <NavIcon name="rooms" />,
				roles: ["admin", "super"],
			},
		],
	},
	{
		title: "TIZIM",
		items: [
			{
				path: "/users",
				label: "Foydalanuvchilar",
				icon: <NavIcon name="user" />,
				roles: ["admin", "super"],
			},
			{
				path: "/audit",
				label: "Audit jurnali",
				icon: <NavIcon name="audit" />,
				roles: ["admin", "super"],
			},
		],
	},
];

export const navAliases: Record<string, NavItem> = {
	"/organizations/new": {
		path: "/organizations",
		label: "Yangi tashkilot",
		icon: <NavIcon name="plus" />,
		roles: ["super"],
		subtitle: "Tashkilot + birinchi admin",
	},
	"/sales/new": {
		path: "/sales",
		label: "Yangi sotuv",
		icon: <NavIcon name="plus" />,
		roles: ["admin", "manager"],
		subtitle: "Sotuv yaratish",
	},
	"/payments/new": {
		path: "/payments",
		label: "To'lov kiritish",
		icon: <NavIcon name="plus" />,
		subtitle: "Kassa operatsiyasi",
	},
	"/payments/early": {
		path: "/payments",
		label: "Erta to'lov",
		icon: <NavIcon name="percent" />,
		roles: ["admin", "manager"],
		subtitle: "Skidka bilan to'lov",
	},
	"/payments/overdue": {
		path: "/payments",
		label: "Grafiklar",
		icon: <NavIcon name="calendar" />,
		subtitle: "Kechikkan va yaqin to'lovlar",
	},
	"/payments/bank": {
		path: "/payments",
		label: "Bank akkount",
		icon: <NavIcon name="bank" />,
		roles: ["admin", "super"],
		subtitle: "Bank rekvizitlari",
	},
	"/payments/discount-scale": {
		path: "/payments",
		label: "Skidka shkalasi",
		icon: <NavIcon name="percent" />,
		roles: ["admin", "super"],
		subtitle: "Erta to'lov sozlamalari",
	},
	"/sms/templates": {
		path: "/sms/balance",
		label: "SMS shablonlari",
		icon: <NavIcon name="file" />,
		roles: ["admin"],
		subtitle: "Xabar matnlari",
	},
	"/sms/auto": {
		path: "/sms/balance",
		label: "Avto-eslatma",
		icon: <NavIcon name="calendar" />,
		roles: ["admin"],
		subtitle: "Avtomatik xabarlar",
	},
	"/sms/send": {
		path: "/sms/balance",
		label: "SMS yuborish",
		icon: <NavIcon name="send" />,
		roles: ["admin", "manager"],
		subtitle: "Yakka xabar",
	},
	"/sms/batch": {
		path: "/sms/balance",
		label: "Ommaviy SMS",
		icon: <NavIcon name="message" />,
		roles: ["admin", "manager"],
		subtitle: "Guruhga yuborish",
	},
	"/sms/logs": {
		path: "/sms/balance",
		label: "SMS loglar",
		icon: <NavIcon name="file" />,
		roles: ["admin"],
		subtitle: "Yuborilgan xabarlar",
	},
	"/sms/reports": {
		path: "/sms/balance",
		label: "SMS hisobot",
		icon: <NavIcon name="chart" />,
		roles: ["admin"],
		subtitle: "SMS statistikasi",
	},
	"/sms/detalization": {
		path: "/sms/balance",
		label: "SMS detalizatsiya",
		icon: <NavIcon name="file" />,
		roles: ["admin"],
		subtitle: "SMS tafsilotlari",
	},
	"/contract": {
		path: "/clients",
		label: "Shartnoma",
		icon: <NavIcon name="file" />,
		subtitle: "Shartnoma generatsiyasi",
	},
	"/roles": {
		path: "/users",
		label: "Rollar va Huquqlar",
		icon: <NavIcon name="shield" />,
		roles: ["admin", "super"],
		subtitle: "Tizim rollari va ruxsatlar",
	},
	"/profile": {
		path: "/profile",
		label: "Profil",
		icon: <NavIcon name="user" />,
		subtitle: "Sozlamalar",
	},
};

export function getVisibleGroups(role: AppRole): NavGroup[] {
	return navGroups
		.map((group) => ({
			...group,
			items: group.items.filter((item) => !item.roles || item.roles.includes(role)),
		}))
		.filter((group) => group.items.length > 0);
}

const routeAccessRules: Array<{
	match: (pathname: string) => boolean;
	rule: RouteAccessRule;
}> = [
	{
		match: (pathname) => pathname === "/dashboard",
		rule: { label: "Bosh sahifa", subtitle: "Umumiy ko'rsatkichlar" },
	},
	{
		match: (pathname) => pathname === "/organizations" || pathname.startsWith("/organizations/"),
		rule: {
			label: "Tashkilotlar",
			roles: ["super"],
			subtitle: "Barcha kompaniyalar",
		},
	},
	{
		match: (pathname) =>
			pathname === "/sections" || (pathname.includes("/blocks") && pathname.includes("/sections")),
		rule: {
			label: "Sektsiyalar",
			roles: ["admin", "super"],
			subtitle: "Blok -> sektsiyalar",
		},
	},
	{
		match: (pathname) => pathname === "/rooms/new",
		rule: {
			label: "Xona qo'shish",
			roles: ["admin", "super"],
			subtitle: "Yangi xona",
		},
	},
	{
		match: (pathname) => pathname === "/sales/new",
		rule: {
			label: "Yangi sotuv",
			roles: ["admin", "manager"],
			subtitle: "Sotuv yaratish",
		},
	},
	{
		match: (pathname) => pathname === "/payments/early",
		rule: {
			label: "Erta to'lov",
			roles: ["admin", "manager"],
			subtitle: "Skidka bilan to'lov",
		},
	},
	{
		match: (pathname) => pathname === "/payments/bank",
		rule: {
			label: "Bank akkount",
			roles: ["admin", "super"],
			subtitle: "Bank rekvizitlari",
		},
	},
	{
		match: (pathname) => pathname === "/payments/discount-scale",
		rule: {
			label: "Skidka shkalasi",
			roles: ["admin", "super"],
			subtitle: "Erta to'lov sozlamalari",
		},
	},
	{
		match: (pathname) =>
			[
				"/sms/balance",
				"/sms/templates",
				"/sms/auto",
				"/sms/logs",
				"/sms/reports",
				"/sms/detalization",
			].includes(pathname),
		rule: {
			label: "SMS",
			roles: ["admin"],
			subtitle: "SMS balans, shablon va hisobotlar",
		},
	},
	{
		match: (pathname) => pathname === "/sms/send" || pathname === "/sms/batch",
		rule: {
			label: "SMS yuborish",
			roles: ["admin", "manager"],
			subtitle: "Qo'lda xabar yuborish",
		},
	},
	{
		match: (pathname) => pathname === "/reports/debts",
		rule: {
			label: "Qarzdorlik",
			roles: ["admin", "super", "manager"],
			subtitle: "Qarzdorlar hisoboti",
		},
	},
	{
		match: (pathname) =>
			[
				"/reports/sales",
				"/reports/payments",
				"/reports/managers",
				"/reports/rooms",
				"/users",
				"/roles",
				"/audit",
			].includes(pathname),
		rule: {
			label: "Boshqaruv",
			roles: ["admin", "super"],
			subtitle: "Hisobotlar va tizim sahifalari",
		},
	},
];

export function getRouteAccess(pathname: string): RouteAccessRule {
	return (
		routeAccessRules.find((item) => item.match(pathname))?.rule ?? {
			label: getActiveNavItem(pathname).label,
			roles: getActiveNavItem(pathname).roles,
			subtitle: getActiveNavItem(pathname).subtitle,
		}
	);
}

const activeNavItemRules: { match: (p: string) => boolean; item: NavItem | string }[] = [
	{
		match: (p) => p.startsWith("/sales/") && p.endsWith("/schedule"),
		item: {
			path: "/sales",
			label: "To'lov grafigi",
			icon: <NavIcon name="calendar" />,
			subtitle: "Shartnoma to'lovlar jadvali",
		},
	},
	{
		match: (p) => p.startsWith("/sales/") && p !== "/sales" && p !== "/sales/new",
		item: {
			path: "/sales",
			label: "Sotuv tafsiloti",
			icon: <NavIcon name="file" />,
			subtitle: "Shartnoma tafsilotlari",
		},
	},
	{
		match: (p) => p.startsWith("/organizations/") && p !== "/organizations",
		item: "/organizations/new",
	},
	{
		match: (p) => p.includes("/blocks") && p.includes("/sections"),
		item: {
			path: "/objects",
			label: "Sektsiyalar",
			icon: <NavIcon name="rooms" />,
			roles: ["admin", "super"],
			subtitle: "Blok -> sektsiyalar",
		},
	},
	{
		match: (p) => p.includes("/blocks"),
		item: {
			path: "/objects",
			label: "Bloklar",
			icon: <NavIcon name="building" />,
			subtitle: "Bino -> bloklar",
		},
	},
	{
		match: (p) => p === "/rooms/new",
		item: {
			path: "/rooms",
			label: "Xona qo'shish",
			icon: <NavIcon name="plus" />,
			roles: ["admin", "super"],
			subtitle: "Yangi xona",
		},
	},
	{
		match: (p) => p.startsWith("/rooms/") && p !== "/rooms",
		item: {
			path: "/rooms",
			label: "Xona tafsiloti",
			icon: <NavIcon name="rooms" />,
			subtitle: "Xona kartochkasi",
		},
	},
	{
		match: (p) => p === "/clients/new",
		item: {
			path: "/clients",
			label: "Mijoz qo'shish",
			icon: <NavIcon name="plus" />,
			subtitle: "Yangi mijoz",
		},
	},
	{
		match: (p) => p.startsWith("/clients/") && p.endsWith("/edit"),
		item: {
			path: "/clients",
			label: "Mijoz tahriri",
			icon: <NavIcon name="team" />,
			subtitle: "Mijoz ma'lumotlarini o'zgartirish",
		},
	},
	{
		match: (p) => p.startsWith("/clients/") && p !== "/clients",
		item: {
			path: "/clients",
			label: "Mijoz tafsiloti",
			icon: <NavIcon name="team" />,
			subtitle: "Mijoz kartochkasi va to'lovlar",
		},
	},
];

export function getActiveNavItem(pathname: string): NavItem {
	const rule = activeNavItemRules.find((r) => r.match(pathname));
	if (rule) {
		return typeof rule.item === "string" ? navAliases[rule.item] : rule.item;
	}

	return (
		navAliases[pathname] ??
		navGroups.flatMap((group) => group.items).find((item) => item.path === pathname) ??
		navGroups[0].items[0]
	);
}
